// What a template opens with when it is picked: the one decision the host used to spell out in handlePick. Pure; it
// follows ../README.md and moves into @gs/gs-integration-component with planCarry.
//
// In order:
// 0. The template's own entry says it was started blank on purpose (Start blank): nothing, whatever the other sources hold.
// 1. The template's own earlier input on this channel, when it holds something typed.
// 2. The working draft (else the last save), when it is for this very template on this channel: reopen it as it stands.
// 3. Whatever of that draft still fits this template (planCarry), with a notice saying what carried.
// 4. Nothing: a fresh form.
import { planCarry } from './carryOver';
import { draftHasValue } from './draftValues';
import { cloneJson, isObject } from './savedJsonMatch';

const copyList = (value) => (Array.isArray(value) ? [...value] : []);

/**
 * A draft in the shape a panel opens with (its `restored` prop) and the library is prefilled from: the panels' own
 * defaults ([] and null) for what the draft lacks, and copies, never references into the draft.
 * @param {object} [draft]
 * @returns {import('./carryOver').CarryPrefill}
 */
export function valuesOf(draft) {
  const d = draft ?? {};
  return {
    params: copyList(d.params),
    headerParams: copyList(d.headerParams),
    mediaUrl: d.mediaUrl || null,
    location: isObject(d.location) ? { ...d.location } : null,
    buttonUrlParam: d.buttonUrlParam || null,
    ctaButtonUrl: d.ctaButtonUrl || null,
    waTemplateJson: isObject(d.waTemplateJson) ? (cloneJson(d.waTemplateJson) ?? null) : null,
  };
}

/**
 * Chooses what a picked template opens with.
 * @param {object} input
 * @param {object} [input.own] - the picked template's own remembered draft on this channel, if any; or the mark Start blank leaves,
 *   `{ startedBlank: true }`, which holds no value and ends the search
 * @param {object} [input.working] - the draft the marketer has now: what the open panel last reported
 * @param {object} [input.saved] - the last saved draft; used when there is no working one
 * @param {{ details?: object }} input.toTemplate - the template picked, a mapTemplate() row
 * @param {{ details?: object }} [input.fromTemplate] - the template the draft was saved on, as listed now; undefined once it is gone
 * @param {string} input.channelId - the channel the template was picked on
 * @returns {{ prefill: (import('./carryOver').CarryPrefill|null), carry: ({ fromName: string, kept: string[] }|null) }}
 *   prefill is what to open with (null: a fresh form). carry is the notice, set only when prefill came from another template.
 */
export function choosePrefill(input) {
  const {
    own, working, saved, toTemplate, fromTemplate, channelId,
  } = input ?? {};
  if (own?.startedBlank) return { prefill: null, carry: null };
  if (own && draftHasValue(own)) return { prefill: valuesOf(own), carry: null };

  const base = working ?? saved;
  if (base && base.channelId === channelId && base.templateId != null
    && String(base.templateId) === String(toTemplate?.details?.id)) {
    return { prefill: valuesOf(base), carry: null };
  }

  const plan = planCarry({
    saved: base, fromTemplate, toTemplate, channelId,
  });
  if (!plan) return { prefill: null, carry: null };
  return {
    prefill: plan.carried,
    carry: { fromName: base.templateName || fromTemplate?.details?.name || 'the saved message', kept: plan.kept },
  };
}
